import { Box, SxProps, TextFieldProps, Theme } from "@mui/material";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import ByzMuiTextField from "./ByzMuiTextField";

type SpeechRecognitionLike = {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
  onresult?: (event: any) => void;
  onstart?: () => void;
  onend?: () => void;
  onerror?: () => void;
  start: () => void;
  stop: () => void;
};

interface ByzMuiTextInputWithMicProps extends Omit<TextFieldProps, "variant"> {
  enableAiFormatting?: boolean;
}

const emitChange = (onChange: TextFieldProps["onChange"], nextValue: string) => {
  if (!onChange) return;

  onChange({
    target: { value: nextValue },
    currentTarget: { value: nextValue },
  } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
};

const normalizeTranscript = (value: unknown) => value?.toString?.().trim?.() ?? "";

const ByzMuiTextInputWithMic: FC<ByzMuiTextInputWithMicProps> = ({
  value,
  onChange,
  disabled,
  sx,
  label,
  multiline = true,
  rows = 3,
  ...props
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const shouldKeepRecordingRef = useRef(false);
  const committedValueRef = useRef(String(value ?? ""));
  const interimValueRef = useRef("");

  useEffect(() => {
    committedValueRef.current = String(value ?? "");
  }, [value]);

  const pushCombinedValue = () => {
    const committed = committedValueRef.current.trim();
    const interim = interimValueRef.current.trim();
    const nextValue = [committed, interim].filter(Boolean).join("\n");
    emitChange(onChange, nextValue);
  };

  const stopRecognition = () => {
    shouldKeepRecordingRef.current = false;
    setIsRecording(false);
    try {
      recognitionRef.current?.stop?.();
    } catch {
    }
  };

  const startRecognition = () => {
    const SpeechRecognitionCtor =
      (globalThis as any).SpeechRecognition ||
      (globalThis as any).webkitSpeechRecognition;

    if (!SpeechRecognitionCtor || disabled) return;

    try {
      if (!recognitionRef.current) {
        const recognition = new SpeechRecognitionCtor();
        recognition.lang = "tr-TR";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event: any) => {
          const results = event?.results;
          if (!results?.length) return;

          const finalParts: string[] = [];
          const interimParts: string[] = [];

          for (let i = 0; i < results.length; i += 1) {
            const transcript = normalizeTranscript(results[i]?.[0]?.transcript);
            if (!transcript) continue;

            if (results[i]?.isFinal) {
              finalParts.push(transcript);
            } else {
              interimParts.push(transcript);
            }
          }

          if (finalParts.length) {
            const previous = committedValueRef.current.trim();
            const appendedFinal = finalParts.join(" ").trim();
            committedValueRef.current = previous
              ? `${previous}\n${appendedFinal}`
              : appendedFinal;
          }

          interimValueRef.current = interimParts.join(" ").trim();
          pushCombinedValue();
        };

        recognition.onstart = () => {
          interimValueRef.current = "";
          committedValueRef.current = String(value ?? "");
          setIsRecording(true);
        };

        recognition.onend = () => {
          if (shouldKeepRecordingRef.current) {
            try {
              recognition.start();
              return;
            } catch {
            }
          }

          interimValueRef.current = "";
          pushCombinedValue();
          setIsRecording(false);
        };

        recognition.onerror = () => {
          shouldKeepRecordingRef.current = false;
          interimValueRef.current = "";
          pushCombinedValue();
          setIsRecording(false);
        };

        recognitionRef.current = recognition as SpeechRecognitionLike;
      }

      shouldKeepRecordingRef.current = true;
      interimValueRef.current = "";
      setIsRecording(true);
      recognitionRef.current.start();
    } catch {
      setIsRecording(false);
    }
  };

  useEffect(() => {
    return () => {
      stopRecognition();
    };
  }, []);

  const handleMicClick = () => {
    if (isRecording) {
      stopRecognition();
      return;
    }
    startRecognition();
  };

  const textFieldSx: SxProps<Theme> = {
    "& .MuiInputBase-root": {
      position: "relative",
      alignItems: multiline ? "flex-start" : "center",
    },
    "& .MuiInputBase-input": {
      paddingRight: "56px",
    },
    "& .MuiInputBase-inputMultiline": {
      paddingRight: "56px",
      paddingBottom: "36px",
    },
    ...((sx as object) || {}),
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <ByzMuiTextField
        {...props}
        multiline={multiline}
        rows={rows}
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        label={typeof label === "string" ? label : undefined}
        sx={textFieldSx}
      />
      <Box
        sx={{
          position: "absolute",
          right: 12,
          bottom: 8,
          width: 44,
          height: 30,
          borderRadius: 999,
          bgcolor: isRecording ? "#4E657F" : "#F4F7FE",
          border: "1px solid",
          borderColor: isRecording ? "#4E657F" : "#1F2937",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0px 1px 3px rgba(15, 23, 42, 0.25)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          "&:hover": {
            bgcolor: disabled
              ? (isRecording ? "#4E657F" : "#F4F7FE")
              : (isRecording ? "#4E657F" : "#E4EBF5"),
          },
        }}
        onClick={disabled ? undefined : handleMicClick}
      >
        <Box
          component="img"
          src="/assets/icons/generals/microphone.svg"
          alt="microphone"
          sx={{
            width: 18,
            height: 18,
            filter: isRecording ? "invert(1)" : "none",
          }}
        />
      </Box>
    </Box>
  );
};

export default ByzMuiTextInputWithMic;
